import _ from "lodash";
import IIndicationsRequest from "../panels/addFOBIndicationPanel/types/IIndicationsRequest";
import TeamsMessage from "../panels/addFOBIndicationPanel/lib/teamsMessage";
import NitroService from "./nitro.service";
import IModes from "../types/IModes";

export const createApiError = (message: string, statusCode: number) => ({
  message,
  statusCode,
});

export type OriginManagerPrice = number | "DISC" | "N/O" | null;

// TODO: untyped code. replace with typed version in multiLocationQuoteUtils.tsx
export const normalizeOriginManagerPrice = (
  value: unknown,
): OriginManagerPrice => {
  if (value == null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  if (normalized === "DISCUSS") {
    return "DISC";
  }

  if (normalized === "NO_OFFER") {
    return "N/O";
  }

  const numericValue = Number(normalized.replace(/,/g, ""));
  return Number.isFinite(numericValue) ? numericValue : null;
};

// TODO: untyped code. replace with typed version in multiLocationQuoteUtils.tsx
export const formatOriginManagerPriceDisplay = (
  value: unknown,
): string | null => {
  const normalized = normalizeOriginManagerPrice(value);
  if (normalized == null) {
    return null;
  }

  return typeof normalized === "number"
    ? normalized.toFixed(0)
    : normalized;
};

type OriginManagerNbComparableQuote = {
  mode?: string | null;
  competitor?: boolean;
  postedPrice?: unknown;
  truckPrice?: unknown;
  railPrice?: unknown;
};

export interface OriginManagerNbStyleDeltaResult {
  backgroundColor: string;
  netbackTextColor: string;
  hasBackgroundChange: boolean;
  deltaText: string;
  deltaColor?: string;
  forceFontSize20: boolean;
}

// Applies origin-manager-specific NB styling and delta behavior based on
// posted/rail/truck price references instead of generic max/min values.
export const evaluateOriginManagerNbStyleAndDelta = (
  quote: OriginManagerNbComparableQuote,
  nbValue: number,
  hasDlvdOfferInput = false,
): OriginManagerNbStyleDeltaResult | null => {
  if (!Number.isFinite(nbValue)) {
    return null;
  }

  const mode = (quote.mode || "").toUpperCase();
  const isKoch = quote.competitor !== true;

  let lowerBoundNormalized: OriginManagerPrice = null;
  if (mode === "TRUCK") {
    lowerBoundNormalized = normalizeOriginManagerPrice(quote.truckPrice);
  } else if (mode === "RAIL") {
    lowerBoundNormalized = normalizeOriginManagerPrice(quote.railPrice);
  }

  const upperBoundNormalized = isKoch
    ? normalizeOriginManagerPrice(quote.postedPrice)
    : null;

  const isDiscussOrNoOffer = (value: OriginManagerPrice): boolean =>
    value === "DISC" || value === "N/O";

  let lowerBound =
    typeof lowerBoundNormalized === "number" ? lowerBoundNormalized : null;
  let upperBound =
    typeof upperBoundNormalized === "number" ? upperBoundNormalized : null;

  const allPricesNull =
    normalizeOriginManagerPrice(quote.postedPrice) == null &&
    normalizeOriginManagerPrice(quote.truckPrice) == null &&
    normalizeOriginManagerPrice(quote.railPrice) == null;

  if (hasDlvdOfferInput && !allPricesNull) {
    if (
      upperBound != null &&
      lowerBound == null &&
      isDiscussOrNoOffer(lowerBoundNormalized)
    ) {
      lowerBound = upperBound;
    } else if (
      lowerBound != null &&
      upperBound == null &&
      isDiscussOrNoOffer(upperBoundNormalized)
    ) {
      upperBound = lowerBound;
    }
  }

  if (lowerBound == null && upperBound == null) {
    return null;
  }

  const neutralStyle: OriginManagerNbStyleDeltaResult = {
    backgroundColor: "transparent",
    netbackTextColor: "#152b36",
    hasBackgroundChange: false,
    deltaText: "",
    forceFontSize20: false,
  };

  if (!isKoch && lowerBound != null) {
    if (nbValue > lowerBound) {
      const delta = Math.round(nbValue - lowerBound);
      return {
        backgroundColor: "rgb(220, 252, 231)",
        netbackTextColor: "rgb(22, 101, 52)",
        hasBackgroundChange: true,
        deltaText: delta === 0 ? "" : ` ${delta > 0 ? `$+${delta}` : `$${delta}`}`,
        deltaColor: "rgb(22, 101, 52)",
        forceFontSize20: true,
      };
    }

    if (nbValue < lowerBound) {
      const delta = Math.round(nbValue - lowerBound);
      return {
        backgroundColor: "rgb(254, 226, 226)",
        netbackTextColor: "rgb(153, 27, 27)",
        hasBackgroundChange: true,
        deltaText: delta === 0 ? "" : ` ${delta > 0 ? `$+${delta}` : `$${delta}`}`,
        deltaColor: "rgb(153, 27, 27)",
        forceFontSize20: true,
      };
    }

    return neutralStyle;
  }

  const isWithinDisplayedRange =
    lowerBound != null &&
    upperBound != null &&
    nbValue < Math.max(lowerBound, upperBound) &&
    nbValue > Math.min(lowerBound, upperBound);

  if (isWithinDisplayedRange) {
    return {
      backgroundColor: "rgba(254, 243, 199, 0.898)",
      netbackTextColor: "#92400E",
      hasBackgroundChange: true,
      deltaText: "",
      forceFontSize20: false,
    };
  }

  if (lowerBound != null && nbValue < lowerBound) {
    const delta = Math.round(nbValue - lowerBound);
    return {
      backgroundColor: "rgb(254, 226, 226)",
      netbackTextColor: "rgb(153, 27, 27)",
      hasBackgroundChange: true,
      deltaText: delta === 0 ? "" : ` ${delta > 0 ? `$+${delta}` : `$${delta}`}`,
      deltaColor: "rgb(153, 27, 27)",
      forceFontSize20: true,
    };
  }

  if (upperBound != null && nbValue > upperBound) {
    const delta = Math.round(nbValue - upperBound);
    return {
      backgroundColor: "rgb(220, 252, 231)",
      netbackTextColor: "rgb(22, 101, 52)",
      hasBackgroundChange: true,
      deltaText: delta === 0 ? "" : ` ${delta > 0 ? `$+${delta}` : `$${delta}`}`,
      deltaColor: "rgb(22, 101, 52)",
      forceFontSize20: true,
    };
  }

  return neutralStyle;
};

export type KochDlvdMode = {
  truck: "px" | "alt";
  rail: "px" | "alt";
};

interface ISortableQuoteForNetback {
  mode: string;
  competitor?: boolean;
  maxPrice?: number | null;
  postedPrice?: number | null;
  truckPrice?: number | null;
  railPrice?: number | null;
  freight?: { totalFreight?: number | null } | null;
}

// TODO: untyped code. replace with typed version in multiLocationQuoteUtils.tsx
export const resolveNetbackForSort = (
  quote: ISortableQuoteForNetback,
  kochDlvdMode: KochDlvdMode,
): number | null => {
  const quoteMode = (quote.mode || "").toUpperCase();
  let rawValue: unknown;

  if (!quote.competitor && quoteMode === "TRUCK") {
    rawValue = kochDlvdMode.truck === "px" ? quote.postedPrice : quote.truckPrice;
  } else if (!quote.competitor && quoteMode === "RAIL") {
    rawValue = kochDlvdMode.rail === "px" ? quote.postedPrice : quote.railPrice;
  } else if (quote.competitor && quoteMode === "TRUCK") {
    rawValue = quote.truckPrice;
  } else if (quote.competitor && quoteMode === "RAIL") {
    rawValue = quote.railPrice;
  }

  const display = formatOriginManagerPriceDisplay(rawValue);
  if (!display || display === "DISC" || display === "N/O") {
    // Fall back to maxPrice display (mirrors desktop getOriginRowPricing fallback)
    const fallback = formatOriginManagerPriceDisplay(quote.maxPrice);
    if (!fallback || fallback === "DISC" || fallback === "N/O") return null;
    const fallbackNum = Number(fallback);
    return Number.isFinite(fallbackNum) ? fallbackNum : null;
  }

  const num = Number(display);
  return Number.isFinite(num) ? num : null;
};


// TODO: untyped code. replace with typed version in multiLocationQuoteUtils.tsx
export const sortQuotesByNetbackAndDlvd = <T extends ISortableQuoteForNetback>(
  quotes: T[],
  kochDlvdMode: KochDlvdMode,
): T[] =>
  [...quotes].sort((a, b) => {
    const netbackA = resolveNetbackForSort(a, kochDlvdMode);
    const netbackB = resolveNetbackForSort(b, kochDlvdMode);
    const hasNetbackA = netbackA !== null;
    const hasNetbackB = netbackB !== null;

    if (hasNetbackA && !hasNetbackB) return -1;
    if (!hasNetbackA && hasNetbackB) return 1;
    if (hasNetbackA && hasNetbackB) {
      const dlvdA = netbackA + (a.freight?.totalFreight ?? 0);
      const dlvdB = netbackB + (b.freight?.totalFreight ?? 0);
      return dlvdA - dlvdB;
    }
    return 0;
  });

export const responseError = (error: any) => {
  const statusCode = _.get(error, "response.status", 0);
  let message = _.get(error, "response.data.message");
  if (!message) {
    const data = _.get(error, "response.data");
    const errorMsg = _.get(error, "message", "Something went wrong");
    const msg = _.get(error, "response.message", errorMsg) || "";
    message = data && typeof data === "string" ? data : msg;
  }
  switch (true) {
    case statusCode === 400:
      message = `Oops! <br><br>Looks like we received a bad request  (${statusCode} error).`;
      break;
    case statusCode === 403:
      // client is forbidde do any action for all api
      message = `Oops!<br><br>The server refuses to process this request (${statusCode} error).`;
      break;
    case statusCode === 404:
      message = `Oops! <br><br>Something went wrong and the page you're looking for cannot be found (${statusCode} error).`;
      break;
    case statusCode === 500: {
      message = `This is unexpected… Error Code 500 <br><br>An error code has occurred and we’ll be up and running shortly. If you need assistance, please contact our Logistics team. `;
      break;
    }
    default:
      message = `We are unable to fulfill your request. Please contact our Logistics team.`;
      break;
  }
  console.error("API Error:", message, "Status Code:", statusCode);
};

export type LogIndicationDependencies = {
  disablePostToTeams: boolean;
  savedNotification: (message: string) => void;
  failedNotification: (message: string, errorMessage: string) => void;
  reload: () => void;
  close: () => void;
};

/**
 * Posts an indication to Teams and/or saves it to the backend service.
 * @param record - The indication data to be processed.
 * @param deps - An object containing all necessary external dependencies.
 */
export const logIndication = async (
  record: IIndicationsRequest,
  deps: LogIndicationDependencies,
) => {
  const {
    disablePostToTeams,
    savedNotification,
    failedNotification,
    reload,
    close,
  } = deps;

  try {
    // POST TO TEAMS
    if (!disablePostToTeams && record.indications[0]?.postToTeams) {
      try {
        await TeamsMessage.postIndication(record);
        savedNotification("Indication posted to Teams.");
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error("Failed to post indication to Teams", err);
        failedNotification("Failed to post indication to Teams.", errorMessage);
        // We throw here to stop the process if Teams posting is critical and fails
        throw new Error("Failed to post indication to Teams.");
      }
    }

    // SAVE INDICATION
    try {
      await NitroService.createIndications(record);
      savedNotification("Successfully created indication.");
    } catch (err) {
      console.error("Failed to create indication", err);
      // This error will be caught by the outer catch block
      throw new Error("Failed to create indication.");
    }

    // On success, reload data and close the panel
    reload();
    close();
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    // This outer catch handles the failure from the "SAVE INDICATION" block
    // or the re-thrown error from the "POST TO TEAMS" block.
    failedNotification("Failed to create indication.", errorMessage);
  }
};

export const formatShipDate = (
  dateString: string,
  position: "start" | "end",
): string => {
  const [monthStr, yearStr] = dateString.split("-");
  const year = parseInt(yearStr, 10);

  const monthMap: { [key: string]: number } = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  const monthIndex = monthMap[monthStr.toLowerCase()];

  if (Number.isNaN(monthIndex) || Number.isNaN(year)) {
    return new Date().toISOString().split("T")[0];
  }

  if (position === "start") {
    const date = new Date(year, monthIndex, 1);
    return date.toISOString().split("T")[0];
  }
  const date = new Date(year, monthIndex + 1, 0);
  return date.toISOString().split("T")[0];
};

export const generateMonthOptions = () => {
  const options = [];
  const date = new Date();
  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });

  for (let i = 0; i < 12; i += 1) {
    const month = monthFormatter.format(date).toLowerCase();
    const year = date.getFullYear();
    options.push(`${month}-${year}`);
    date.setMonth(date.getMonth() + 1);
  }
  return options;
};

export const formatRequestedAtDateTime = (requestedAt: unknown): string => {
  if (!requestedAt || requestedAt === "-") return "-";

  let parsedDate: Date | null = null;

  if (typeof requestedAt === "string") {
    const parsed = new Date(requestedAt);
    parsedDate = Number.isNaN(parsed.getTime()) ? null : parsed;
  } else if (typeof requestedAt === "object") {
    const raw = requestedAt as {
      year?: number;
      month?: number;
      day?: number;
      hour?: number;
      minute?: number;
      second?: number;
    };

    const year = Number(raw.year) || 0;
    const month = Number(raw.month) || 0;
    const day = Number(raw.day) || 0;

    if (year && month && day) {
      parsedDate = new Date(
        year,
        month - 1,
        day,
        Number(raw.hour) || 0,
        Number(raw.minute) || 0,
        Number(raw.second) || 0,
      );
    }
  }

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) return "-";

  const datePart = parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} · ${timePart}`;
};

export const toModes = (
  inboundModes: string[],
  outboundModes: string[],
): IModes => {
  const safeInboundModes = inboundModes || [];
  const safeOutboundModes = outboundModes || [];

  return {
    TRUCK: {
      name: "TRUCK",
      inbound: safeInboundModes.indexOf("TRUCK") > -1,
      outbound: safeOutboundModes.indexOf("TRUCK") > -1,
    },
    RAIL: {
      name: "RAIL",
      inbound: safeInboundModes.indexOf("RAIL") > -1,
      outbound: safeOutboundModes.indexOf("RAIL") > -1,
    },
    VESSEL: {
      name: "VESSEL",
      inbound: safeInboundModes.indexOf("VESSEL") > -1,
      outbound: safeOutboundModes.indexOf("VESSEL") > -1,
    },
    BARGE: {
      name: "BARGE",
      inbound: safeInboundModes.indexOf("BARGE") > -1,
      outbound: safeOutboundModes.indexOf("BARGE") > -1,
    },
    PIPELINE: {
      name: "PIPELINE",
      inbound: safeInboundModes.indexOf("PIPELINE") > -1,
      outbound: safeOutboundModes.indexOf("PIPELINE") > -1,
    },
  };
};

// Scrolls an element to the center of the viewport area not covered by the mobile keyboard.
export const scrollElementAboveKeyboard = (
  element: HTMLElement | null,
  scrollContainerSelector: string,
) => {
  if (!element) return;

  const scrollContainer = element.closest<HTMLElement>(
    scrollContainerSelector,
  );
  if (!scrollContainer) return;

  let hasScrolled = false;
  const performScroll = () => {
    if (hasScrolled) return;
    hasScrolled = true;

    const { visualViewport } = window;
    const visibleHeight = visualViewport?.height ?? window.innerHeight;
    const visibleTop = visualViewport?.offsetTop ?? 0;

    const rect = element.getBoundingClientRect();
    const elementCenter = rect.top + rect.height / 2;
    const visibleCenter = visibleTop + visibleHeight / 2;

    scrollContainer.scrollBy({
      top: elementCenter - visibleCenter,
      behavior: "smooth",
    });
  };

  // Wait for the keyboard animation to finish resizing the viewport before measuring it.
  const handleResize = () => {
    performScroll();
    window.visualViewport?.removeEventListener("resize", handleResize);
  };
  window.visualViewport?.addEventListener("resize", handleResize);
  // Fallback in case no resize fires (keyboard already open, or visualViewport unsupported).
  setTimeout(() => {
    window.visualViewport?.removeEventListener("resize", handleResize);
    performScroll();
  }, 350);
};
